import { useState, useEffect } from 'react';
import { waitForApiToken } from '../utils/waitForApiToken';

/**
 * Hook to fetch and parse equity curve data for completed backtests
 *
 * @param {string} backtestId - The backtest ID to fetch data for
 * @param {Function} getApiToken - Function to get the API auth token
 * @returns {Object} { chartData, loading, error }
 */
export default function useBacktestEquityCurve(backtestId, getApiToken) {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Skip if missing required params
    if (!backtestId || !getApiToken) {
      return;
    }

    let cancelled = false;

    const fetchChartData = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = await waitForApiToken(getApiToken);
        if (cancelled) return;
        if (!token) {
          throw new Error('Authentication required');
        }
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/backtests/${backtestId}/chart-series?series=equity&limit=1000`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.ok) {
          // Provide specific error messages based on HTTP status
          if (response.status === 403) {
            throw new Error('ACCESS_DENIED');
          } else if (response.status === 404) {
            throw new Error('NOT_FOUND');
          } else {
            throw new Error(`HTTP_ERROR_${response.status}`);
          }
        }

        const data = await response.json();

        if (cancelled) return;

        const parsedData = parseChartSeries(data);
        setChartData(parsedData);
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching equity curve:', err);
          // Provide user-friendly error messages based on error type
          if (err.message === 'ACCESS_DENIED') {
            setError('Unable to access backtest results. Please try refreshing the page.');
          } else if (err.message === 'NOT_FOUND') {
            setError('Backtest results not found. The backtest may still be running.');
          } else {
            setError('Failed to load chart data. Please try again.');
          }
          setChartData([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchChartData();

    return () => {
      cancelled = true;
    };
  }, [backtestId, getApiToken]);

  return { chartData, loading, error };
}

function parseChartSeries(payload) {
  const points = Array.isArray(payload?.points) ? payload.points : [];

  return points
    .map((point) => {
      const y = typeof point?.y === 'number' ? point.y : Number(point?.y);
      if (!point?.x || !Number.isFinite(y)) {
        return null;
      }
      return {
        date: String(point.x),
        y
      };
    })
    .filter(Boolean)
    .sort((a, b) => new Date(a.date) - new Date(b.date));
}
